/**
 * Shine — a soft band of light that sweeps once across its parent whenever
 * `trigger` changes (not on first render). Feedback for "the value under you
 * just changed" (side switch, new line, new book). The parent needs
 * `overflow: 'hidden'` and its own border radius. Reduced motion: no-op.
 *
 *   <View style={{ overflow: 'hidden' }}><Shine trigger={`${side}|${line}`} />...</View>
 */
import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { timing, prefersReducedMotion } from '../../utils/motion';

const BAND = 140;

export default function Shine({ trigger, tint = 'rgba(255,255,255,0.14)', width = 900 }) {
    const x = useRef(new Animated.Value(0)).current;
    const first = useRef(true);

    useEffect(() => {
        if (first.current) { first.current = false; return undefined; }
        if (prefersReducedMotion()) return undefined;
        x.setValue(0);
        const anim = timing(x, 1, { duration: 760, easing: 'out' });
        anim.start();
        return () => anim.stop();
    }, [trigger, x]);

    return (
        <Animated.View
            style={[
                StyleSheet.absoluteFill,
                {
                    pointerEvents: 'none',
                    opacity: x.interpolate({ inputRange: [0, 0.08, 0.8, 1], outputRange: [0, 1, 1, 0] }),
                    transform: [{ translateX: x.interpolate({ inputRange: [0, 1], outputRange: [-BAND, width] }) }],
                },
            ]}
        >
            <LinearGradient
                colors={['rgba(255,255,255,0)', tint, 'rgba(255,255,255,0)']}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={{ width: BAND, height: '100%', transform: [{ skewX: '-18deg' }] }}
            />
        </Animated.View>
    );
}
